import * as React                  from 'react';
 
import { useSearchParams         } from 'react-router-dom';

import { useAlert                } from 'react-alert';

import { useTheme                } from 'styled-components';
 
import { ArrowBackVector         } from 'src/components/vector/arrow-back';
import { FloppyVector            } from 'src/components/vector/floppy';

import { BlockUI                 } from 'src/components/common/block-ui';
import { Dialog                  } from 'src/components/common/dialog';
import { TwoActionDialog         } from 'src/components/common/two-action-dialog';
import { OneActionDialog         } from 'src/components/common/one-action-dialog';
import { Typography              } from 'src/components/common/typography';

import { NavBar                  } from 'src/components/features/common/nav-bar';

import { DefectsList             } from 'src/components/features/inspection/edit/defects-list';
import { DuplicateDialog         } from 'src/components/features/inspection/edit/duplicate-dialog';
import { CVCheckPieceFilesDialog } from 'src/components/features/inspection/edit/cv-check-piece-files-dialog';
import { EndingDialog            } from 'src/components/features/inspection/edit/ending-dialog';

import { InspectionEditLayout, 
         Screen,
         ScreenBody,
         layout_light,
         layout_dark             } from 'src/containers/inspection/edit/layout';

import { InspectionViewDialog    } from 'src/containers/inspection/view';

import { Urls                    } from 'src/providers/routing';

import { InspectionState, 
         ModeControlDefect       } from 'src/services/api/types/inspection';

import { AppDispatch,
         useAppDispatch,
         useAppSelector,
         storeApi                } from 'src/store';

import { AsyncOpStatus           } from 'src/common';



const SAVE_NOW_FALSE = { isSaveEditedPiece: false, isSaveEditedComplaint: false };

type InspectionEditScreenElement = { layout: InspectionEditLayout; dispatch: AppDispatch; }

const Navigation: React.FC<InspectionEditScreenElement> = ({ layout, dispatch }) => {
  const navbarText = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralMainNavbarText);
  const isSaveDraftVisible = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralIsDraftSaveVisible);
  
  return (
    <React.Fragment>
      <NavBar
        variant = { layout.navbarVariant }
        label = { navbarText }
        startButtons={[
          {
            id: 'back',
            vector: <ArrowBackVector />,
            action: () => dispatch(storeApi.inspection.edit.actions.endingOpened())
          }
        ]}
        endButtons={isSaveDraftVisible ? [
          {
            id: 'save',
            vector: <FloppyVector />,
            action: () => dispatch(storeApi.inspection.edit.actions.endingOpened())
          }
        ] : []}
      />
    </React.Fragment>
  )
}

export const InspectionEditScreen = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const layout = theme.colorScheme === 'light' ? layout_light : layout_dark;

  const alert = useAlert();

  const [openWithoutErrors, setOpenWithoutErrors] = React.useState<boolean>(false);

  const opStatus = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralOpStatus);
  const opStatusLabel = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralOpStatusLabel);
  const isDefectFilledError = useAppSelector(storeApi.inspection.edit.selectors.selectDefectIsFilledError);
  const defectGuid = useAppSelector(storeApi.inspection.edit.selectors.selectDefectExpandedGuid);
  const defectChangedManually = useAppSelector(storeApi.inspection.edit.selectors.selectDefectChangedManually);
  const defectId = useAppSelector((state) => storeApi.inspection.edit.selectors.selectDefectDefectId(state, defectGuid)) ?? 0;

  const bootOpStarted = React.useRef<boolean>(false);

  const [errorOpened, setErrorOpened] = React.useState<boolean>(false);
  const [defectWarnOpened, setDefectWarnOpened] = React.useState<boolean>(false);
  const [defectWarnContent, setDefectWarnContent] = React.useState<React.ReactNode>(null);
  const [errorText, setErrorText] = React.useState<string>('');

  const [searchParams] = useSearchParams();
  const guid = searchParams.get('guid');
  const state = searchParams.get('state') as InspectionState;
  const withDefectRaw = searchParams.get('withDefect');
  const withDefect = withDefectRaw === 'Y';
  const isTransferOnlyRaw = searchParams.get('isTransferOnly');
  const isTransferOnly = isTransferOnlyRaw === 'Y';
  const isChangeToComplaintRaw = searchParams.get('isChangeToComplaint');
  const isChangeToComplaint = isChangeToComplaintRaw === 'Y';
  const isChangeToComplaintFinanceRaw = searchParams.get('isChangeToComplaintFinance');
  const isChangeToComplaintFinance = isChangeToComplaintFinanceRaw === 'Y';
  const isChangeComplaintCostsRaw = searchParams.get('isChangeComplaintCosts');
  const isChangeComplaintCosts = isChangeComplaintCostsRaw === 'Y';
  const isChangeComplaintAllRaw = searchParams.get('isChangeComplaintAll');
  const isChangeComplaintAll = isChangeComplaintAllRaw === 'Y';
  const isSendRaw = searchParams.get('isSend');
  const isSend = isSendRaw === 'Y';

  const isChangeRequirements = isChangeToComplaint || isChangeToComplaintFinance || isChangeComplaintCosts;

  React.useEffect(() => {
    if (defectChangedManually)
    {
      if ([19, 34, 43].includes(defectId))
      {
        setDefectWarnContent(
          <Typography variant='blackw400s16lh22'>
            В поле <Typography variant='redw400s16lh22'>"Количество брака"</Typography> укажите&nbsp;
            <Typography variant='redw400s16lh22'>разницу</Typography> между фактическим весом и весом&nbsp;
            по документу/бирке.
          </Typography>
        );
        setDefectWarnOpened(true);
      }
      if ([15, 52].includes(defectId))
      {
        setDefectWarnContent(
          <Typography variant='blackw400s16lh22'>
            В разделе <Typography variant='redw400s16lh22'>"Информация о продукции"</Typography> укажите&nbsp;
            <Typography variant='redw400s16lh22'>данные позиции по документу.</Typography>&nbsp;
            В описание добавьте данные по фактической позиции (фото бирки).
          </Typography>
        );
        setDefectWarnOpened(true);
      }
      dispatch(storeApi.inspection.edit.actions.defectDefectChangedManuallyReset());
    }
  }, [defectId, defectChangedManually]);

  React.useEffect(
    () => {
      if (bootOpStarted.current)
      {
        return;
      }

      bootOpStarted.current = true;

      if (isSend)
      {
        if (isDefectFilledError)
        {
          setErrorOpened(true);
          setErrorText('Не заполнены дефекты. Перед отправкой необходимо откорректировать осмотр!');
        }
        else
        {
          dispatch(storeApi.inspection.edit.async.saveInspectionAsync({
            isComplete: true,
            modeControlDefect: isChangeRequirements ? ModeControlDefect.CHANGING_REQUIREMENTS : ModeControlDefect.COMPLETE,
            isTransferOnly: false,
          }))
            .unwrap()
            .then(() => {
              alert.success('Осмотр успешно сохранен');
              Urls.MainInspections.build().navigate();
            })
            .catch((error) => {
              if (error.redirectToList)
              {
                (error.isWarning ? alert.info : alert.error).call(null, error.error);
                Urls.MainInspections.build().navigate();
              }
              else
              {
                setErrorOpened(true);
                setErrorText(error.error);
              }
            });
        }
      }
      else
      {
        if (
          guid === null
          ||
          state === null
          ||
          withDefectRaw === null
          ||
          isTransferOnlyRaw === null
          ||
          isChangeToComplaintRaw === null
          ||
          isChangeToComplaintFinanceRaw === null
          ||
          isChangeComplaintCostsRaw === null
          ||
          isChangeComplaintAllRaw === null
        )
        {
          setOpenWithoutErrors(true);
          return;
        }

        dispatch(storeApi.inspection.edit.async.buildForEditAsync({
          guid,
          state,
          withDefect,
          isTransferOnly,
          isChangeToComplaint,
          isChangeToComplaintFinance,
          isChangeComplaintCosts,
          isChangeComplaintAll,
        }))
          .unwrap()
          .then((result) => {
            switch (result.operationResult)
            {
              case 'get_error':
                alert.error('Не удалось открыть осмотр.');
                return Urls.MainInspections.build().navigate();
              case 'error_fixed':
                alert.info(result.operationResultMessage);
                return Urls.MainInspections.build().navigate();
              case 'error_revocation':
                alert.info(result.operationResultMessage);
                return Urls.MainInspections.build().navigate();
              case 'data_updated':
                alert.info(result.operationResultMessage);
                setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_FALSE)), 200);
                break;
            }

            setOpenWithoutErrors(true);

            if (isTransferOnly)
            {
              if (isDefectFilledError)
              {
                setErrorOpened(true);
                setErrorText('Не заполнены дефекты. Перед отправкой необходимо откорректировать осмотр!');
              }
              else
              {
                dispatch(storeApi.inspection.edit.async.saveInspectionAsync({
                  isComplete: state !== InspectionState.DRAFT,
                  modeControlDefect: state === InspectionState.DRAFT ? ModeControlDefect.SKIP : ModeControlDefect.COMPLETE,
                  isTransferOnly: true,
                }))
                  .unwrap()
                  .then(() => {
                    alert.success('Осмотр успешно сохранен');
                    Urls.MainInspections.build().navigate();
                  })
                  .catch((error) => {
                    if (error.redirectToList)
                    {
                      (error.isWarning ? alert.info : alert.error).call(null, error.error);
                      Urls.MainInspections.build().navigate();
                    }
                    else
                    {
                      setErrorOpened(true);
                      setErrorText(error.error);
                    }
                  });
              }
            }
          });
      }
    },
    []
  );

  const onSaveModeSelect = (saveMode: 'complete' | 'draft' | 'exit' | 'cancel') => {
    dispatch(storeApi.inspection.edit.actions.endingClosed());

    switch (saveMode)
    {
      case 'complete':
        dispatch(storeApi.inspection.edit.async.saveInspectionAsync({
          isComplete: true,
          modeControlDefect: isChangeRequirements ? ModeControlDefect.CHANGING_REQUIREMENTS : ModeControlDefect.COMPLETE,
        }))
          .unwrap()
          .then(() => {
            alert.success('Осмотр успешно сохранен');
            Urls.MainInspections.build().navigate();
          })
          .catch((error) => {
            if (error.redirectToList)
            {
              (error.isWarning ? alert.info : alert.error).call(null, error.error);
              Urls.MainInspections.build().navigate();
            }
            else
            {
              setErrorOpened(true);
              setErrorText(error.error);
            }
          });
        break;
      case 'draft':
        dispatch(storeApi.inspection.edit.async.saveInspectionAsync({
          isComplete: false,
          modeControlDefect: ModeControlDefect.SKIP,
        }))
          .unwrap()
          .then(() => {
            alert.success('Черновик сохранен');
            Urls.MainInspections.build().navigate();
          })
          .catch((error) => {
            if (error.redirectToList)
            {
              (error.isWarning ? alert.info : alert.error).call(null, error.error);
              Urls.MainInspections.build().navigate();
            }
            else
            {
              setErrorOpened(true);
              setErrorText(error.error);
            }
          });
        break;
      case 'exit':
        dispatch(storeApi.inspection.edit.async.deleteImmediatelyAsync());
        Urls.MainInspections.build().navigate();
        break;
      case 'cancel':
        break;
    }
  }

  const onActionErrorHandler = () => {
    setErrorOpened(false);
    setErrorText('');
  }

  const onActionDefectWarnHandler = () => {
    setDefectWarnOpened(false);
    setDefectWarnContent(null);
  }

  return (
    <Screen layout = { layout }>
      <Navigation layout = { layout } dispatch = { dispatch } />
      <ScreenBody layout = { layout }>
        {openWithoutErrors && (
          <DefectsList variant = { layout.defectsListVariant } />
        )}
      </ScreenBody>
      <DuplicateDialog variant = { layout.duplicateDialogVariant } />
      <CVCheckPieceFilesDialog variant = { layout.cvCheckPieceFilesDialogVariant } />
      <EndingDialog variant = { layout.endingDialogVariant } onSaveModeSelect = { onSaveModeSelect } />
      <BlockUI variant = { layout.blockUiVariant } isOpened = { opStatus === AsyncOpStatus.BUSY } message = { opStatusLabel }  />
      <InspectionViewDialog variant = { layout.inspectionViewVariant } />
      <TwoActionDialog 
        variant = { layout.errorDialog }
        opened = { errorOpened }
        caption = 'Ошибка'
        message = { errorText }
        isDanger
        withoutCancel
        onAction = { onActionErrorHandler }
      />
      <Dialog variant = { layout.defectWarnDialog } isOpened = { defectWarnOpened }>
        <OneActionDialog 
          variant = { layout.defectWarn }
          caption = 'Внимание!'
          message = { defectWarnContent }
          actionLabel = 'Ok'
          onAction = { onActionDefectWarnHandler }
        />
      </Dialog>
    </Screen>
  );
};
